import { useState, FC, MouseEvent } from 'react'
import { ResolvedFunction } from '../../types/functions'
import { Hash, Check } from 'lucide-react'

interface FunctionHeaderProps {
  func: ResolvedFunction
  onSelectCategory?: (categoryName: string, moduleName: string) => void
}

export const FunctionHeader: FC<FunctionHeaderProps> = ({ func, onSelectCategory }) => {
  const [copiedAnchor, setCopiedAnchor] = useState(false)

  const copyAnchorLink = (e: MouseEvent) => {
    e.preventDefault()
    const url = `${window.location.origin}${window.location.pathname}#${func.anchorId}`
    navigator.clipboard.writeText(url)
    setCopiedAnchor(true)
    setTimeout(() => setCopiedAnchor(false), 2000)
  }

  const getBadgeClass = (badge: string) => {
    switch (badge) {
      case 'FUNC':
        return 'ns-badge-func'
      case 'BROWSER':
        return 'ns-badge-browser'
      case 'ASYNC':
        return 'ns-badge-async'
      default:
        return 'ns-badge-default'
    }
  }

  return (
    <div className="ns-function-header">
      {/* Breadcrumb Path */}
      <div className="ns-function-breadcrumbs">
        <span className="ns-breadcrumb-module">{func.module}</span>
        <span className="ns-breadcrumb-sep">/</span>
        {onSelectCategory ? (
          <button
            type="button"
            className="ns-breadcrumb-category-btn"
            onClick={() => onSelectCategory(func.category, func.module)}
            title={`View all ${func.category} functions`}
          >
            {func.category}
          </button>
        ) : (
          <span className="ns-breadcrumb-category">{func.category}</span>
        )}
      </div>

      {/* Main Title & Badge */}
      <div className="ns-function-title-row">
        <div className="ns-function-title-group">
          <span className={`ns-function-badge-lg ${getBadgeClass(func.badge)}`}>
            {func.badge}
          </span>
          <h2 id={func.anchorId} className="ns-function-title">
            {func.displayName}
          </h2>
          <button
            type="button"
            onClick={copyAnchorLink}
            className="ns-function-anchor-btn"
            title="Copy link to function"
          >
            {copiedAnchor ? <Check size={16} className="text-forest" /> : <Hash size={16} />}
          </button>
        </div>
      </div>

      {/* Description / Summary */}
      {func.summary && (
        <p className="ns-function-summary">{func.summary}</p>
      )}

      {/* Remarks */}
      {func.remarks && (
        <div className="ns-function-remarks">
          <div className="ns-remarks-label">Notes & Remarks</div>
          <div className="ns-remarks-content">{func.remarks}</div>
        </div>
      )}
    </div>
  )
}
